import React, { useCallback } from "react";
import { LazyQueryNetworkBoundrary } from "../../../../components/mobile/QueryNetworkBoundrary";
import { RoutineEditScreenProps } from "../../RoutineEditMain/RoutineEditNavigation";
import { Team } from "../../../../types/Team";
import { useModalInsets } from "../../../../components/mobile/useModalInsets";
import { graphql, useLazyLoadQuery } from "react-relay";
import { Box, SectionHeader } from "arkh";
import { FormattedMessage } from "react-intl";
import { TeamSelectList } from "widgets/src/TeamSelectList";
import { RoutineEditChecklistSectionIssueTeamQuery } from "./__generated__/RoutineEditChecklistSectionIssueTeamQuery.graphql";
import {
  useRoutineEditState,
  useRoutineEditStateSetParams,
} from "../../State/RoutineEditState";
import { useNavigationHeader } from "@/src/common/navigationHeaderOptions";
import { getSectionTitle } from "../../getSectionTitle";

export function RoutineEditChecklistSectionIssueTeam(
  props: RoutineEditScreenProps<"RoutineEditChecklistSectionIssueTeam">
) {
  const { sections = [], team, } = useRoutineEditState();
  const setParams = useRoutineEditStateSetParams();
  const sectionId = props.route.params.sectionId;
  const section = sections.find(({ id }) => id === sectionId);
  const teamId = section?.assignedTeam?.id;

  useNavigationHeader({
    type: "DONE",
    onDone: () => {
      props.navigation.goBack();
    },
    defaultContentStyle: true,
    title: section ? getSectionTitle(section) : "",
  });

  if (!team) {
    return null;
  }
  return (
    <LazyQueryNetworkBoundrary>
      {(fetchKey) => (
        <InnerRoutineEditChecklistSectionIssueTeam
          routineTeam={team}
          fetchKey={fetchKey}
          setTeam={(team) => {
            setParams({
              sections: sections.map((_section) =>
                _section.id === sectionId
                  ? { ..._section, assignedTeam: team }
                  : _section
              ),
            });
          }}
          teamId={teamId}
        />
      )}
    </LazyQueryNetworkBoundrary>
  );
}

function InnerRoutineEditChecklistSectionIssueTeam({
  teamId,
  routineTeam,
  setTeam,
  fetchKey,
}: {
  teamId: string | undefined;
  setTeam(team: Team | null): void;
  routineTeam: Team;
  fetchKey: string;
}) {
  const insets = useModalInsets();
  const data = useLazyLoadQuery<RoutineEditChecklistSectionIssueTeamQuery>(
    graphql`
      query RoutineEditChecklistSectionIssueTeamQuery {
        store @required(action: THROW) {
          publicTeams {
            id
            color
            name
            icon
            isPrivate
            ...TeamSelectList_Team
          }
        }
      }
    `,
    {},
    {
      fetchKey,
      fetchPolicy: "store-and-network",
    }
  );

  const onItemPress = useCallback(
    (itemId: string, selected: boolean) => {
      const team = data.store.publicTeams.find(({ id }) => id === itemId);
      if (team) {
        setTeam(
          selected
            ? null
            : {
                color: team.color,
                icon: team.icon as any,
                id: team.id,
                name: team.name,
                isPrivate: team.isPrivate,
              }
        );
      }
    },
    [data.store.publicTeams, setTeam]
  );

  return (
    <TeamSelectList
      ListHeaderComponent={
        <Box spaceTop="medium">
          <SectionHeader>
            <SectionHeader.Title>
              <FormattedMessage
                defaultMessage="Assign team"
                description="RoutineEditChecklistSectionIssueTeam titel"
              />
            </SectionHeader.Title>
            <SectionHeader.Description>
              <FormattedMessage
                defaultMessage="The selected team will be assigned all issues from the section."
                description="RoutineEditChecklistSectionIssueTeam description"
              />
            </SectionHeader.Description>
          </SectionHeader>
        </Box>
      }
      disabled={(itemId) => itemId === routineTeam.id}
      insets={{
        bottom: insets.bottom,
      }}
      onItemPress={onItemPress}
      selected={teamId ? [teamId, routineTeam.id] : [routineTeam.id]}
      teams={data.store.publicTeams}
    />
  );
}
